import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './SessionsPage.css';

const API_URL = 'http://vtym-douane.test/backend/wp-json';

const SessionsPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatut, setFilterStatut] = useState('all');

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = async () => {
        try {
            const response = await fetch(`${API_URL}/douane/v1/sessions`);
            const data = await response.json();

            if (data.success) {
                setSessions(data.sessions);
            }
        } catch (err) {
            console.error('Erreur:', err);
        } finally {
            setLoading(false);
        }
    };

    const getSessionStatus = (session) => {
        const now = new Date();
        const debut = new Date(session.date_debut);
        const fin = new Date(session.date_fin);

        if (session.statut === 'annulee') return 'Annulée';
        if (session.statut === 'terminee') return 'Terminée';
        if (now >= debut && now <= fin) return 'En cours';
        if (now < debut) return 'À venir';
        return 'Terminée';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'En cours': return 'live';
            case 'À venir': return 'upcoming';
            case 'Terminée': return 'finished';
            case 'Annulée': return 'cancelled';
            default: return '';
        }
    };

    const handleJoinSession = (sessionId) => {
        if (!isAuthenticated) {
            alert('Vous devez être connecté pour rejoindre une session');
            navigate('/login');
            return;
        }
        navigate(`/classroom/${sessionId}`);
    };

    const filteredSessions = sessions.filter(session => {
        if (filterStatut === 'all') return true;
        return getSessionStatus(session) === filterStatut;
    });

    if (loading) {
        return (
            <div className="sessions-page">
                <div className="container">
                    <div className="loading">Chargement des sessions...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="sessions-page">
            <div className="container">
                
                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1>📅 Sessions de Formation</h1>
                        <p>Participez aux classes virtuelles en direct</p>
                    </div>
                </div>

                {/* Filtres */}
                <div className="filters">
                    <button 
                        className={filterStatut === 'all' ? 'active' : ''}
                        onClick={() => setFilterStatut('all')}
                    >
                        Toutes
                    </button>
                    <button 
                        className={filterStatut === 'En cours' ? 'active' : ''}
                        onClick={() => setFilterStatut('En cours')}
                    >
                        🔴 En cours
                    </button>
                    <button 
                        className={filterStatut === 'À venir' ? 'active' : ''}
                        onClick={() => setFilterStatut('À venir')}
                    >
                        🕐 À venir
                    </button>
                    <button 
                        className={filterStatut === 'Terminée' ? 'active' : ''}
                        onClick={() => setFilterStatut('Terminée')}
                    >
                        ✅ Terminées
                    </button>
                </div>

                {/* Liste des sessions */}
                {filteredSessions.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📹</div>
                        <h3>Aucune session trouvée</h3>
                        <p>Il n'y a pas de session pour ce filtre</p>
                    </div>
                ) : (
                    <div className="sessions-grid">
                        {filteredSessions.map(session => {
                            const status = getSessionStatus(session);
                            const statusColor = getStatusColor(status);
                            const dateDebut = new Date(session.date_debut);
                            const dateFin = new Date(session.date_fin);

                            return (
                                <div key={session.id} className="session-card">
                                    <div className={`session-status ${statusColor}`}>
                                        {status}
                                    </div>

                                    <div className="session-content">
                                        <h3>{session.titre}</h3>
                                        <p className="formation-name">{session.formation_titre}</p>

                                        <div className="session-details">
                                            <div className="detail-item">
                                                <span className="icon">📅</span>
                                                <span>{dateDebut.toLocaleDateString('fr-FR', { 
                                                    weekday: 'long', 
                                                    year: 'numeric', 
                                                    month: 'long', 
                                                    day: 'numeric' 
                                                })}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="icon">🕐</span>
                                                <span>
                                                    {dateDebut.toLocaleTimeString('fr-FR', { 
                                                        hour: '2-digit', 
                                                        minute: '2-digit' 
                                                    })} - {dateFin.toLocaleTimeString('fr-FR', { 
                                                        hour: '2-digit', 
                                                        minute: '2-digit' 
                                                    })}
                                                </span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="icon">👨‍🏫</span>
                                                <span>{session.formateur || 'Formateur'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="icon">👥</span>
                                                <span>{session.participants} / {session.participants_max} participants</span>
                                            </div>
                                        </div>

                                        {status === 'En cours' && (
                                            <button 
                                                onClick={() => handleJoinSession(session.id)}
                                                className="btn-join live"
                                            >
                                                🔴 Rejoindre maintenant
                                            </button>
                                        )}

                                        {status === 'À venir' && (
                                            <button 
                                                onClick={() => handleJoinSession(session.id)}
                                                className="btn-join upcoming"
                                            >
                                                📅 S'inscrire
                                            </button>
                                        )}

                                        {status === 'Terminée' && (
                                            <button 
                                                className="btn-join finished"
                                                disabled
                                            >
                                                ✅ Session terminée
                                            </button>
                                        )}

                                        {status === 'Annulée' && (
                                            <button 
                                                className="btn-join cancelled"
                                                disabled
                                            >
                                                ❌ Annulée
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>
        </div>
    );
};

export default SessionsPage;